import { CrossSection, RebarZone } from './types'

// Curated default reinforcement for the predefined concrete sections. These are sensible
// starting points (not final engineering) so a freshly selected section is analysable.
export interface RebarSpec {
  cover: number                                            // mm clear cover
  bottom: { diameter: number; count: number }             // main tension bars
  top?: { diameter: number; count: number }               // top bars (omit = none)
  stirrups?: { diameter: number; spacing: number; legs: number } // omit = none (slabs)
}

// Beams: cover 35 mm, 2-leg stirrups. Plate strips (1 m wide): cover 25 mm, no stirrups.
export const CONCRETE_REBAR_PRESETS: Record<string, RebarSpec> = {
  CONCRETE_150x300: { cover: 35, bottom: { diameter: 12, count: 2 }, top: { diameter: 10, count: 2 }, stirrups: { diameter: 8, spacing: 150, legs: 2 } },
  CONCRETE_200x300: { cover: 35, bottom: { diameter: 16, count: 2 }, top: { diameter: 12, count: 2 }, stirrups: { diameter: 8, spacing: 150, legs: 2 } },
  CONCRETE_200x400: { cover: 35, bottom: { diameter: 16, count: 3 }, top: { diameter: 12, count: 2 }, stirrups: { diameter: 8, spacing: 200, legs: 2 } },
  CONCRETE_200x500: { cover: 35, bottom: { diameter: 16, count: 3 }, top: { diameter: 12, count: 2 }, stirrups: { diameter: 8, spacing: 200, legs: 2 } },
  CONCRETE_250x400: { cover: 35, bottom: { diameter: 16, count: 3 }, top: { diameter: 12, count: 2 }, stirrups: { diameter: 8, spacing: 200, legs: 2 } },
  CONCRETE_250x500: { cover: 35, bottom: { diameter: 20, count: 3 }, top: { diameter: 12, count: 2 }, stirrups: { diameter: 8, spacing: 200, legs: 2 } },
  CONCRETE_250x600: { cover: 35, bottom: { diameter: 20, count: 3 }, top: { diameter: 16, count: 2 }, stirrups: { diameter: 8, spacing: 200, legs: 2 } },
  CONCRETE_300x500: { cover: 35, bottom: { diameter: 20, count: 3 }, top: { diameter: 16, count: 2 }, stirrups: { diameter: 10, spacing: 200, legs: 2 } },
  CONCRETE_300x600: { cover: 35, bottom: { diameter: 20, count: 4 }, top: { diameter: 16, count: 2 }, stirrups: { diameter: 10, spacing: 200, legs: 2 } },
  CONCRETE_300x700: { cover: 35, bottom: { diameter: 20, count: 4 }, top: { diameter: 16, count: 2 }, stirrups: { diameter: 10, spacing: 200, legs: 2 } },
  CONCRETE_350x700: { cover: 35, bottom: { diameter: 25, count: 4 }, top: { diameter: 16, count: 2 }, stirrups: { diameter: 10, spacing: 200, legs: 2 } },
  CONCRETE_400x800: { cover: 35, bottom: { diameter: 25, count: 5 }, top: { diameter: 16, count: 3 }, stirrups: { diameter: 12, spacing: 200, legs: 2 } },
  CONCRETE_PLATE_1000x150: { cover: 25, bottom: { diameter: 12, count: 7 }, top: { diameter: 10, count: 5 } },
  CONCRETE_PLATE_1000x180: { cover: 25, bottom: { diameter: 12, count: 7 }, top: { diameter: 10, count: 5 } },
  CONCRETE_PLATE_1000x200: { cover: 25, bottom: { diameter: 12, count: 7 }, top: { diameter: 10, count: 5 } },
  CONCRETE_PLATE_1000x220: { cover: 25, bottom: { diameter: 16, count: 6 }, top: { diameter: 12, count: 5 } },
  CONCRETE_PLATE_1000x250: { cover: 25, bottom: { diameter: 16, count: 7 }, top: { diameter: 12, count: 5 } },
  CONCRETE_PLATE_1000x300: { cover: 25, bottom: { diameter: 16, count: 7 }, top: { diameter: 12, count: 6 } }
}

const FALLBACK_COVER = 40
const FALLBACK_DIAMETER = 16

// Keyed preset when the section is a known predefined one; otherwise a generic default
// (3 bottom / 2 top Ø16) so custom/unkeyed sections still get reasonable reinforcement.
export function defaultRebarSpecFor(crossSection: CrossSection): RebarSpec {
  const keyed = crossSection.key ? CONCRETE_REBAR_PRESETS[crossSection.key] : undefined
  if (keyed) {
    return keyed
  }
  return {
    cover: crossSection.effectiveCover ?? FALLBACK_COVER,
    bottom: { diameter: FALLBACK_DIAMETER, count: 3 },
    top: { diameter: FALLBACK_DIAMETER, count: 2 }
  }
}

// Bar-centroid position relative to the section centroid (mm, +up): inset from the
// nearest face by clear cover + half the bar diameter.
function layerZ(heightMillimeters: number, cover: number, diameter: number, isTop: boolean): number {
  const inset = heightMillimeters / 2 - cover - diameter / 2
  return isTop ? inset : -inset
}

// One rebar zone spanning the whole beam, with z positions derived from the section height.
export function buildRebarZones(crossSection: CrossSection, spec: RebarSpec, beamLengthMeters: number): RebarZone[] {
  const layers = [{ z: layerZ(crossSection.height, spec.cover, spec.bottom.diameter, false), diameter: spec.bottom.diameter, count: spec.bottom.count }]
  if (spec.top) {
    layers.push({ z: layerZ(crossSection.height, spec.cover, spec.top.diameter, true), diameter: spec.top.diameter, count: spec.top.count })
  }
  const zone: RebarZone = { start: 0, end: beamLengthMeters, cover: spec.cover, layers }
  if (spec.stirrups) {
    zone.stirrups = { diameter: spec.stirrups.diameter, spacing: spec.stirrups.spacing, legs: spec.stirrups.legs }
  }
  return [zone]
}
